import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

import moment from 'moment'

import { formatCurrency, successMessage } from './Message';
import Swal from 'sweetalert2';
import axios from 'axios';
import { reversal } from './BaseURL';



const ReversalDialog = ({open, onClose, transactionData = null  ,user }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');


 const reversalReasons = [
  { value: 'Duplicate Payment', label: 'Duplicate Payment' },
  { value: 'Incorrect Amount Charged', label: 'Incorrect Amount Charged' },
  { value: 'Service Not Rendered', label: 'Service Not Rendered' },
  { value: 'Investigation Error / Not Done', label: 'Investigation Error / Not Done' },
  { value: 'Medication Error / Not Dispensed', label: 'Medication Error / Not Dispensed' },
  { value: 'Service Error', label: 'Service Error' },
  { value: 'Other Service Issue', label: 'Other Service Issue' },
  { value: 'Patient Request', label: 'Patient Request' },
  { value: 'Billing Error', label: 'Billing Error' },
  { value: 'System Error', label: 'System Error' },
  { value: 'other', label: 'Other (Please specify)' },
];

 const todayStr = moment().format('YYYY-MM-DD'); 


const transaction = transactionData || {};
const canReverse = user == transaction.paid_by && todayStr==transaction.date && (transaction.status=='paid' || transaction.status=='Paid' ) ;


 const handleReversal = async () => {
    if (!selectedReason) {
        setError('Please select a reason for the reversal');
        return;
    }

    if (selectedReason === 'other' && !customReason.trim()) {
        setError('Please specify the reason for reversal');
        return;
    }


    setLoading(true);
    setError('');

    try {
        const result = await Swal.fire({
            title: 'Confirm / Cancel',
            text: "Are You Sure You Want To Reverse The Selected Bill Information",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes Confirm'
        });

        if (result.isConfirmed) {
            Swal.fire({
                title: 'Processing.......',
                text: 'Please wait',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            
            const reversalPayload = {
                
                trans_code: transaction.trans_code,      
                table_state: transaction.table_state,      
                amount: parseFloat(transaction.amount),  
                id: transaction.id,         
                receipt: transaction.receipt,  
                charge:transaction.details,
                
                details: selectedReason == 'other' ? customReason.trim() : selectedReason,
                req_by: 'Current User', 
                paid_by: transaction.paid_by,    
                date: new Date().toISOString().split('T')[0], 
                status: 'Reversal Requested'
            };

            console.log('Reversal payload:', reversalPayload);

            const response = await axios.post(reversal, reversalPayload);

            
            Swal.close();

            if (response.data.success) {
                successMessage('Save', response.data.message);
                
                
                setSelectedReason('');
                setCustomReason('');
                onClose(true);
            } else {
                throw new Error(response.data.message || 'Reversal failed');
            }
        } else {
           
            return;
        }

    } catch (err) {
        console.error('Reversal error:', err);
        
      
        Swal.close();
        
       
        const errorMessage = err.response?.data?.message || 'Failed to process reversal';
        
        setError(errorMessage);
        
        await Swal.fire({
            title: 'Error',
            text: errorMessage,
            icon: 'error'
        });
        
    } finally {
        setLoading(false);
    }
};

  const handleClose = () => {
    if (!loading) {
      setSelectedReason('');
      setCustomReason('');
      setError('');
      onClose(false);
    }
  };

 
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                p: 1,
                bgcolor: 'error.light',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <WarningIcon sx={{ color: 'error.main', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              Reverse Transaction
            </Typography>
          </Box>
          {!loading && (
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Warning Alert */}
        <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Warning</AlertTitle>
            This transaction reversal requires <strong>approval from a supervisor</strong>.
            <br /><br />
            Please enter <strong>approver credentials</strong> or send a request for authorization.
            <br /><br />
            <strong>Reversal is only allowed on the same day</strong> the transaction was made.
            <br /><br />
            Only the <strong>original user</strong> who posted/paid the transaction can initiate the reversal.
            <br /><br />
            <strong>Only transactions with status "PAID" can be reversed.</strong>
        </Alert>

        {/* Transaction Details Card */}
        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight={600} mb={2}>
            Transaction Details
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
              <Grid item xs={6} md={6} size={4}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <ReceiptIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Transaction Id
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {transaction.receipt}
                </Typography>
              </Grid>

              <Grid item xs={6}  md={6} size={4}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Patient Id
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  {transaction.id}
                </Typography>
              </Grid>

              <Grid item xs={6} size={4}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Transaction Date
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {new Date(transaction.date).toLocaleDateString()}
                </Typography>
              </Grid>

              

              <Grid item xs={6}  md={6} size={4}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Request By
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  {transaction.req_by}
                </Typography>
              </Grid>

               <Grid item xs={6}  md={6} size={4}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Paid By
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  {transaction.paid_by}
                </Typography>
              </Grid>

              <Grid item xs={6} size={4}>
                <Typography variant="caption" color="text.secondary" fontWeight={400} display="block" mb={1}>
                  Amount
                </Typography>
                <Typography  color="error.main" fontWeight={700}>
                  {formatCurrency(transaction.amount)}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={500} display="block">
                  Narration
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {transaction.details}
                </Typography>
              </Box>
              <Chip
                label={transaction.status}
                color="success"
                size="small"
                variant="outlined"
              />
            </Box>
          </Paper>
        </Box>

        {/* Reason Selection */}
        <FormControl fullWidth sx={{ mb: 2 }} error={error && !selectedReason}>
          <InputLabel id="reason-select-label">Reason for Reversal *</InputLabel>
          <Select
            labelId="reason-select-label"
            value={selectedReason}
            label="Reason for Reversal *"
            onChange={(e) => {
              setSelectedReason(e.target.value);
              if (error) setError('');
            }}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            {reversalReasons.map((reason) => (
              <MenuItem key={reason.value} value={reason.value}>
                {reason.label}
              </MenuItem>
            ))}
          </Select>
          {error && !selectedReason && (
            <FormHelperText>{error}</FormHelperText>
          )}
        </FormControl>

        {/* Custom Reason Input */}
        {selectedReason === 'other' && (
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Please specify the reason *"
            placeholder="Please provide a detailed explanation for the reversal..."
            value={customReason}
            onChange={(e) => {
              setCustomReason(e.target.value);
              if (error) setError('');
            }}
            disabled={loading}
            error={error && selectedReason === 'other' && !customReason.trim()}
            helperText={
              error && selectedReason === 'other' && !customReason.trim()
                ? error
                : `${customReason.length}/500`
            }
            inputProps={{ maxLength: 500 }}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        )}

        {/* Error Alert */}
        {error && !(error && !selectedReason) && !(error && selectedReason === 'other' && !customReason.trim()) && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Info Alert */}
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> This reason will be permanently recorded in the transaction log and audit trail for compliance purposes.
          </Typography>
        </Alert>
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          color="inherit"
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
       <Button
        onClick={handleReversal}
        disabled={
            loading ||
            !canReverse || // 🔒 enforce user/date rules
            !selectedReason ||
            (selectedReason === 'other' && !customReason.trim())
        }
        variant="contained"
        color="error"
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        sx={{ minWidth: 160, borderRadius: 2 }}
        >
        {loading ? 'Processing...' : 'Reverse Transaction'}
        </Button>

      </DialogActions>
    </Dialog>
  );
};



export default ReversalDialog;

